"use client";

import Image from "next/image";
import { type ReactNode, useEffect, useState } from "react";

import { Button } from "@/common/components-2/button";
import { MigrationLink } from "@/common/components-2/migration-link";
import { Section } from "@/common/components-2/section";
import { SectionHeading } from "@/common/components-2/section-heading";

interface HeroProps {
  title?: {
    icon?: string;
    text: string;
  };
  heading: string | ReactNode;
  description: string | ReactNode;
  buttons: {
    label: string;
    href: string;
  }[];
  image: {
    src: string;
    alt: string;
    width: number;
    height: number;
  }[];
  classes?: {
    heading?: {
      heading?: string;
    };
  };
}

function AnimatedImage({ image, currentImageIndex }: { image: HeroProps["image"]; currentImageIndex: number }) {
  return (
    <div className="relative aspect-square flex-1">
      {image.map(({ src, alt, width, height }, index) => (
        <Image
          key={src}
          className="absolute max-w-full rounded-2xl transition-opacity duration-500 ease-in-out xl:w-[632px]"
          src={src}
          alt={alt}
          width={width}
          height={height}
          style={{ opacity: index === currentImageIndex ? 1 : 0 }}
        />
      ))}
    </div>
  );
}

function StaticImage({ image }: { image: HeroProps["image"][0] }) {
  return (
    <div className="flex-1">
      <Image
        className="aspect-square max-w-full rounded-2xl xl:w-[632px]"
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
      />
    </div>
  );
}

export function Hero({ title, heading, description, buttons, image, classes }: HeroProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const imageChangeTimer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % image.length);
    }, 3000);

    return () => clearInterval(imageChangeTimer);
  }, [image.length]);

  return (
    <Section classes={{ container: "pb-14 max-md:pt-4", section: "text-left" }}>
      <div className="flex w-full flex-col gap-10 md:flex-row-reverse md:pt-8 lg:items-center lg:gap-16">
        {image.length > 1 ? <AnimatedImage image={image} currentImageIndex={currentImageIndex} /> : null}
        {image.length === 1 ? <StaticImage image={image[0] as HeroProps["image"][0]} /> : null}
        <div className="flex-1 md:max-w-[520px]">
          <SectionHeading heading={heading} title={title} classes={{ heading: classes?.heading?.heading }} />
          <p className="mt-6 text-base">{description}</p>
          <div className="mt-10 flex flex-col gap-4 md:flex-row">
            {buttons.map(({ label, href }, index) => (
              <Button
                key={label}
                size="lg"
                className="max-md:w-full"
                variant={index === 0 ? "primary" : "secondary"}
                asChild
              >
                <MigrationLink href={href}>{label}</MigrationLink>
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
